/** Имя админа зала в «Допработах и админствах» дашборда ОО — тап открывает меню (11.10.2026,
 *  владелец: «в дашборде тап по админу зала должен давать возможность назначить второго админа зала,
 *  удалить старого, назначить нового»):
 *  «＋ Второй админ» (пока на филиале один), «⇄ Заменить», «✕ Снять» (с подтверждением).
 *  Сервер: PUT /api/assignments/hall-admins — правит текущую строку, старая доска её не откатит;
 *  Telegram — сразу, если день уже отправляли (новому сводка с «Принять», снятому «снят»), иначе — с «Отправить». */
import { useEffect, useRef, useState, type ReactElement } from "react";
import { api } from "../../api/client";
import { ConfirmModal, Dropdown, type DropdownItem } from "../../components/shared";
import type { OoHallAdminRow } from "../../hooks/ooDayExtras";
import css from "./dashboard.module.css";
import hcss from "./hallAdminTap.module.css";

interface RawStaff { id: string; name: string; role?: string; status?: string; custom_perms?: string }
interface RawRow { operator?: string; operator_id?: string; branch_id?: string; time_from?: string | null; time_to?: string | null }
interface EditResp { ok?: boolean; error?: string; draft?: boolean; halladmins?: RawRow[]; notify?: { sent: number; names: string[]; failed: { name: string; reason: string }[] } | null }

/** Кого можно ставить админом — как на доске (useOoDay: активные операторы, ОО и DC). */
const canAdmin = (s: RawStaff) => s.status === "active" && (s.role === "operator" || (s.custom_perms ?? "").includes("operator") || s.role === "oo" || s.role === "dc")
  && !/"board_hidden"\s*:\s*true/.test(s.custom_perms ?? "");   // 16.10: «не показывать в расстановке» (HR)
const toRow = (h: RawRow): OoHallAdminRow => ({ operator: h.operator ?? "?", operatorId: h.operator_id ?? "", branch: h.branch_id ?? "", timeFrom: (h.time_from ?? "").slice(0, 5), timeTo: (h.time_to ?? "").slice(0, 5) });

function resultText(r: EditResp): string {
  if (r.draft) return "Сохранено. Расстановку на этот день ещё не отправляли — сообщение уйдёт с «Отправить».";
  const n = r.notify;
  const parts = [n?.names.length ? `Отправлено: ${n.names.join(", ")}` : "Сохранено"];
  if (n?.failed.length) parts.push(`НЕ доставлено: ${n.failed.map((f) => `${f.name} (${f.reason})`).join(", ")}`);
  return parts.join(" · ");
}

export function HallAdminTap({ row, rows, date, onDone }: {
  row: OoHallAdminRow; rows: OoHallAdminRow[]; date: string;
  /** rows — только если ответ про текущий день (иначе null); changed — сервер правку принял (доску перечитать). */
  onDone: (rows: OoHallAdminRow[] | null, info: string, changed: boolean) => void;
}): ReactElement {
  const [menu, setMenu] = useState<HTMLElement | null>(null);
  const [pick, setPick] = useState<{ anchor: HTMLElement; mode: "add" | "replace" } | null>(null);
  const [confirm, setConfirm] = useState(false);
  const [staff, setStaff] = useState<RawStaff[]>([]);
  const [busy, setBusy] = useState(false);
  /* ответ пришёл после смены даты или ухода с экрана — не применяем (чужой день) */
  const alive = useRef(true); const dateRef = useRef(date); dateRef.current = date;
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);   /* StrictMode монтирует дважды — флаг поднимаем заново */
  const br = row.branch.toUpperCase();
  const twoHere = rows.filter((r) => r.branch === row.branch).length >= 2;

  const send = async (body: Record<string, unknown>) => {
    setBusy(true);
    const reqDate = date;
    try {
      const r = await api<EditResp>("/api/assignments/hall-admins", { method: "PUT", body: { date, branch: row.branch, ...body } });
      const fresh = alive.current && dateRef.current === reqDate;   /* итог рассылки не глотаем и для другого дня (02.09) */
      onDone(fresh ? (r.halladmins ?? []).map(toRow) : null, `${fresh ? "" : `${reqDate.slice(8, 10)}.${reqDate.slice(5, 7)}: `}${resultText(r)}`, true);
    } catch (e) {
      onDone(null, `Не получилось: ${(e as Error).message || "ошибка сети"}`, false);
    } finally { if (alive.current) setBusy(false); }
  };

  /* список открываем уже загруженным: Dropdown встаёт по месту один раз, пустой — встал бы не туда */
  const openPick = (mode: "add" | "replace") => {
    const anchor = menu;
    if (!anchor) return;
    setBusy(true);
    api<{ results?: RawStaff[] }>("/api/staff")
      .then((r) => { if (!alive.current) return; setStaff((r.results ?? []).filter(canAdmin)); setPick({ anchor, mode }); })
      .catch(() => { if (alive.current) { setMenu(null); onDone(null, "Не удалось загрузить сотрудников — попробуйте ещё раз", false); } })
      .finally(() => { if (alive.current) setBusy(false); });
  };

  const busyMeta = new Map(rows.map((r) => [r.operatorId, `админ ${r.branch.toUpperCase()}`]));
  const people: DropdownItem[] = [...staff].sort((a, b) => a.name.localeCompare(b.name, "ru"))
    .map((s) => ({ id: s.id, label: s.name, meta: busyMeta.get(s.id), disabled: busyMeta.has(s.id) }));
  const actions: DropdownItem[] = [
    { id: "add", label: "＋ Второй админ на этот филиал", meta: twoHere ? "уже двое" : undefined, disabled: twoHere },
    { id: "replace", label: "⇄ Заменить на другого" },
    { id: "remove", label: "✕ Снять с админства" },
  ];

  return (
    <>
      <span className={css.dwIco}>◆</span>
      <button type="button" className={`${css.dwNm} ${hcss.name}`} disabled={busy} title="Тап — второй админ, заменить или снять"
        onClick={(e) => setMenu(e.currentTarget)}>{row.operator}</button>
      {/* оверлеи — внутри строки с долгим нажатием (окно «за период»): касание в них до строки не доходит */}
      <span className={hcss.layer} onPointerDown={(e) => e.stopPropagation()}>
      <Dropdown open={!!menu && !pick && !busy} anchor={menu} multi={false} title={`${row.operator} · админ ${br}`} items={actions}
        onCommit={(ids) => { const id = String(ids[0] ?? ""); if (id === "remove") setConfirm(true); else if (id === "add" || id === "replace") openPick(id); }}
        onClose={() => { if (!pick && !busy) setMenu(null); }} />
      <Dropdown open={!!pick} anchor={pick?.anchor ?? null} multi={false}
        title={pick?.mode === "add" ? `Второй админ ${br}` : `Вместо ${row.operator} — админ ${br}`} items={people}
        onCommit={(ids) => {
          const id = String(ids[0] ?? ""); const mode = pick?.mode;
          if (!id || !mode) return;
          void send(mode === "add" ? { action: "add", operator_id: id } : { action: "replace", operator_id: row.operatorId, new_operator_id: id });
        }}
        onClose={() => { setPick(null); setMenu(null); }} />
      {confirm ? (
        <ConfirmModal tone="danger" title={`Снять ${row.operator} с админства ${br}?`} actionLabel="Снять"
          onAction={() => { setConfirm(false); setMenu(null); void send({ action: "remove", operator_id: row.operatorId }); }}
          onCancel={() => { setConfirm(false); setMenu(null); }}>
          Если расстановку на этот день уже отправляли, ему придёт «снят». Админство за этот день ему не оплатится —
          если он уже отработал часть смены, лучше добавить второго админа и каждому задать время.
        </ConfirmModal>
      ) : null}
      </span>
    </>
  );
}
